import { useCallback, useEffect, useState } from "react";
import { getClientUrl } from "shared/utils/getClientUrl";

type CheckStatus = "pass" | "fail" | "skipped";

interface ServerChecks {
  bearerFormat: CheckStatus;
  metadataValid: CheckStatus;
  waTypeKnown: CheckStatus;
  refererPresent: boolean;
  referer: string | null;
  origin: string | null;
  embedUrl: string | null;
  host: string | null;
  organizationFound: CheckStatus;
  domainMatch: CheckStatus;
  matchedSource: "origin" | "referer" | "wa-embed-url" | null;
  widgetProvided: boolean;
  widgetFound: CheckStatus;
  widgetOwnerMatchesOrg: CheckStatus;
  widgetPaused: CheckStatus;
}

interface ServerResult {
  ok: boolean;
  checks: ServerChecks;
  supportCode: string | null;
  hint: string | null;
}

interface ClientChecks {
  elementRegistered: boolean;
  widgetIdPresent: boolean;
  widgetIdFormat: boolean;
  accessIdPresent: boolean;
  accessIdFormat: boolean;
  referrerAvailable: boolean;
  referrer: string;
  pageUrl: string;
  inIframe: boolean;
}

interface NetworkChecks {
  apiReachable: "pass" | "fail" | "loading";
  apiDetail: string | null;
  cdnReachable: "pass" | "fail" | "loading";
  cdnDetail: string | null;
  mapboxReachable: "pass" | "fail" | "warn" | "loading";
  mapboxDetail: string | null;
}

export interface TripperDebugPanelState {
  loading: boolean;
  clientChecks: ClientChecks;
  networkChecks: NetworkChecks;
  serverResult: ServerResult | null;
  serverError: string | null;
}

const IS_24_HEX = /^[a-f0-9]{24}$/i;

function runClientChecks(widgetId: string, accessId: string): ClientChecks {
  let inIframe = false;
  try {
    inIframe = window.top !== window.self;
  } catch {
    inIframe = true;
  }

  return {
    elementRegistered: !!customElements.get("whereabouts-tripper-widget"),
    widgetIdPresent: !!widgetId,
    widgetIdFormat: IS_24_HEX.test(widgetId ?? ""),
    accessIdPresent: !!accessId,
    accessIdFormat: IS_24_HEX.test(accessId ?? ""),
    referrerAvailable: !!document.referrer,
    referrer: document.referrer || "(none)",
    pageUrl: window.location.href,
    inIframe,
  };
}

export function useTripperDebugPanel(
  widgetId: string,
  accessId: string,
): TripperDebugPanelState & { copyReport: () => Promise<void> } {
  const [loading, setLoading] = useState(true);
  const [serverResult, setServerResult] = useState<ServerResult | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [networkChecks, setNetworkChecks] = useState<NetworkChecks>({
    apiReachable: "loading",
    apiDetail: null,
    cdnReachable: "loading",
    cdnDetail: null,
    mapboxReachable: "loading",
    mapboxDetail: null,
  });

  const clientChecks = runClientChecks(widgetId, accessId);

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_SERVER_URL;

    const checkApi = async () => {
      try {
        const res = await fetch(
          `${apiBase}/api/widget-config/${encodeURIComponent(widgetId)}`,
        );
        if (res.ok) {
          setNetworkChecks((prev) => ({
            ...prev,
            apiReachable: "pass",
            apiDetail: null,
          }));
        } else if (res.status === 403) {
          const body = await res.json().catch(() => null);
          const isPaused = body?.error === "widget_paused";
          setNetworkChecks((prev) => ({
            ...prev,
            apiReachable: isPaused ? "pass" : "fail",
            apiDetail: isPaused ? "Widget is paused" : `HTTP ${res.status}`,
          }));
        } else {
          setNetworkChecks((prev) => ({
            ...prev,
            apiReachable: "fail",
            apiDetail: `HTTP ${res.status}`,
          }));
        }
      } catch (err) {
        setNetworkChecks((prev) => ({
          ...prev,
          apiReachable: "fail",
          apiDetail:
            err instanceof Error ? err.message : "Network error",
        }));
      }
    };

    const checkCdn = async () => {
      try {
        const clientUrl = getClientUrl();
        const res = await fetch(
          `${clientUrl}/widget/tripper/v1/locales/en/tripper.json`,
          { method: "HEAD" },
        );
        setNetworkChecks((prev) => ({
          ...prev,
          cdnReachable: res.ok ? "pass" : "fail",
          cdnDetail: res.ok ? null : `HTTP ${res.status}`,
        }));
      } catch (err) {
        setNetworkChecks((prev) => ({
          ...prev,
          cdnReachable: "fail",
          cdnDetail:
            err instanceof Error ? err.message : "Network error",
        }));
      }
    };

    const checkMapbox = async () => {
      try {
        const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
        if (!token) {
          setNetworkChecks((prev) => ({
            ...prev,
            mapboxReachable: "warn",
            mapboxDetail: "No Mapbox token configured",
          }));
          return;
        }
        const res = await fetch(
          `https://api.mapbox.com/tokens/v2?access_token=${token}`,
        );
        setNetworkChecks((prev) => ({
          ...prev,
          mapboxReachable: res.ok ? "pass" : "warn",
          mapboxDetail: res.ok ? null : `HTTP ${res.status}`,
        }));
      } catch {
        setNetworkChecks((prev) => ({
          ...prev,
          mapboxReachable: "warn",
          mapboxDetail: "Blocked — map tiles may not load",
        }));
      }
    };

    const fetchDiagnostics = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${apiBase}/embeddable/widget/diagnostics`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${widgetId}`,
              metadata: JSON.stringify({
                type: "ORGANIZATION",
                entityId: accessId,
              }),
              "wa-type": "tripperWidget",
              "wa-embed-url": window.location.href,
            },
            body: JSON.stringify({
              widgetId: widgetId || undefined,
            }),
          },
        );
        const data = await res.json();
        setServerResult(data as ServerResult);
        setServerError(null);
      } catch (err) {
        setServerError(
          err instanceof Error
            ? err.message
            : "Failed to reach the diagnostics endpoint.",
        );
        setServerResult(null);
      } finally {
        setLoading(false);
      }
    };

    checkApi();
    checkCdn();
    checkMapbox();
    fetchDiagnostics();
  }, [widgetId, accessId]);

  const copyReport = useCallback(async () => {
    const report = {
      timestamp: new Date().toISOString(),
      pageUrl: clientChecks.pageUrl,
      referrer: clientChecks.referrer,
      inIframe: clientChecks.inIframe,
      widgetId,
      accessId,
      clientChecks,
      networkChecks,
      serverResult,
      serverError,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
    } catch {
      console.warn(
        "[Whereabouts Debug] Clipboard write failed. Report:",
        report,
      );
    }
  }, [clientChecks, networkChecks, serverResult, serverError, widgetId, accessId]);

  return {
    loading,
    clientChecks,
    networkChecks,
    serverResult,
    serverError,
    copyReport,
  };
}
